import React, { useContext, useEffect, useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
   Button, ButtonGroup, Col, FormGroup, Input, Label,
   Modal, ModalBody, ModalFooter, ModalHeader, Row, Table
} from "reactstrap";
import _ from "lodash";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FilterSelect, ItemDropdownMenu, ItemTypeahead, ServiceThreadSpinner } from "../index";
import AsyncSelect from "react-select/async";
import { api, constants, dateHelpers, filterHelpers, helpers, UserContext } from "../../utils";
import { DesktopContainer } from "../../layouts";
import {
   useGetActiveMachines,
   useGetTotalSpindles,
   useGetPlanningProdGridData,
   useGetMachineSetupForPlanning,
   useGetItemClasses,
} from "../../hooks";
import { toast } from "react-toastify";
import DebounceInput from "react-debounce-input";
import { ColValue, ErrorDiv } from "./index";
import Select from "react-select";
import classNames from "classnames";

const CHILD_OPT = { value: 0, label: "-- Select --", parent: 0 };

const SELECT_OPTION = { value: 0, label: "-- Select --" };

const DELETE_DTO = {
   Id: 0,
   ReasonId: 0,
   ReasonSubId: 0,
   Note: ""
}

// Copied at Edit or Delete, before a date picker replaces dateFrom / dateTo.
const stampSeenWindow = (row) => ({
   seenDateFrom: row.dateFrom,
   seenDateTo: row.dateTo,
   seenSpindles: row.spindlesUsed,
});

const REPORT_CLASS_TYPES = {
   ByItem: 0,
   ByMachine: 1,
   ByDay: 2,
};

const REPORT_FILE_TYPES = {
   PDF: 0,
   XLSX: 2,
};

const SHIFT_TYPES = {
   EightHour: 0,
   TwelveHour: 1,
};

// The schedule form's field labels. Declared here, not inside PlanningSchedule, so React keeps the same component
// between renders instead of rebuilding every label on each keystroke.
function ColPrompt({ title }) {
   return (
      <Col xxl={4} className="text-white">
         <h5 className="text-end">{title}:</h5>
      </Col>
   );
}

export default function PlanningSchedule(props) {
   const { currentUser } = useContext(UserContext);
   const canEdit = currentUser?.isAdmin || currentUser?.isPowerUser || currentUser?.isProductionEdit;
   const showItemFilter = currentUser?.isAdmin || currentUser?.isPowerUser || currentUser?.isProductionEdit || currentUser?.isProductionView;
   const showRpts = currentUser?.isAdmin || currentUser?.isPowerUser;
   const [childList, setChildList] = useState([]); // child problems unfiltered
   const [childSubs, setChildSubs] = useState([]); // child problems filtered by parent reason id
   const [comment, setComment] = useState("");
   const [dateRange, setDateRange] = useState({
      Start: new Date(),
      End: new Date(),
   });
   const [editId, setEditId] = useState(0);
   const [deleteSeen, setDeleteSeen] = useState(null);
   const [isLoading, setIsLoading] = useState();
   const [item, setItem] = useState(null);
   const [ends, setEnds] = useState(1);
   const [itemClassCodes, setItemClassCodes] = useState([]);
   const [machineInfo, setMachineInfo] = useState({
      MachineSelect: null,
      MachineNumber: null,
      ToAssign: 0,
      Side: "",
   });
   const [maintenanceEdit, setMaintenanceEdit] = useState({ index: -1 });
   const [pphInterval, setPPHInterval] = useState({
      pph: 0,
      pounds: 0,
      totalPounds: 0,
   });
   const [reasonId, setReasonId] = useState(0);
   const [reasons, setReasons] = useState([]);
   const [reasonSubId, setSubId] = useState(0);
   const [refreshData, setRefreshData] = useState(false);
   const [reportPayload, setReportPayload] = useState({
      Plan: {},
      ReportClass: REPORT_CLASS_TYPES.ByItem,
      ReportType: REPORT_FILE_TYPES.PDF,
      ReportShift: SHIFT_TYPES.EightHour,
   });
   const [scheduledMaintenanceList, setScheduledMaintenanceList] = useState([]);
   const [selectedEdit, setSelectedEdit] = useState({ index: -1 });
   const [selectedOption, setSelectedOption] = useState("");
   const [selectedSide, setSelectedSide] = useState("A");
   const [timeInterval, setTimeInterval] = useState({
      days: 1,
      hours: 16,
      totalHours: 16,
   });
   const [timeRange, setTimeRange] = useState({
      From: "07:00",
      To: "23:00",
      ValidFrom: true,
      ValidTo: true,
   });
   const [updateId, setUpdateId] = useState(0);
   const [updateItem, setUpdateItem] = useState(null);
   const hideItem = selectedOption === "maintenance" ? true : false;
   const viewUnScheduled = selectedOption === "unscheduled" ? true : false;
   const modalClassName = currentUser.theme ? `${currentUser.theme}` : "lightTheme";
   const [filters, setFilters] = useState([
      { filterName: "reasonId", value: SELECT_OPTION },
      { filterName: "child", value: CHILD_OPT }
   ]);

   useEffect(() => {
      api
         .fetch("Production/GetScheduleReasons")
         .then(r => {
            if (r.data.success) {
               const reasonList = _.concat(
                  [SELECT_OPTION],
                  _.map(r.data.reasons, (r) => ({ value: r.id, label: r.name }))
               );
               const subList = _.concat(
                  _.map(r.data.childReasons, (s) => ({ value: s.id, label: s.name, parent: s.parentId }))
               );
               return {
                  reasonList: reasonList,
                  subList: subList
               };
            }
            else { throw new Error(r); }
         })
         .then(({ reasonList, subList }) => {
            setReasons(reasonList);
            setChildList(subList);
            setChildSubs(CHILD_OPT); // This will populate when a parent reason is chosen
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentUser]);

   const fixChildren = async (id) => {
      let newPblmList = [CHILD_OPT]; // Default option
      var subs = _.pickBy(childList, (e) => _.includes([id], e.parent));
      if (subs) { // There are child problems for the selected reason code
         newPblmList = _.concat(
            [CHILD_OPT],
            _.map(subs, (s) => ({ value: s.value, label: s.label, parent: s.parent }))
         );
         setChildSubs(newPblmList);
      }
   };

   async function onReasonChange(changedFilter) {
      // Populate selected reason children & clear lingering values
      await fixChildren(changedFilter.value);
      setSubId(0);

      // Set the filters to the selected reason and the default child
      let newFilters = filterHelpers.getFilters(filters, changedFilter);
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "child",
         value: CHILD_OPT,
      });
      setFilters(newFilters);
      setReasonId(changedFilter.value);
   }

   function onSubChange(changedFilter) {
      let newFilters = filterHelpers.getFilters(filters, changedFilter);
      setFilters(newFilters);
      setSubId(changedFilter.value);
   }

   const handleOptionClick = (e) => {
      const currentTargetValue = e.target.value;
      if (selectedOption === currentTargetValue) {
         setSelectedOption("");
         e.target.checked = false;
      } else {
         if (currentTargetValue === "maintenance") {
            setCurrentItem(null);
         }
         setSelectedOption(currentTargetValue);
      }
   };

   const handleRadioChange = (event) => {
      const value = parseInt(event.target.value);
      setReportPayload({
         ...reportPayload,
         ReportType: value,
      });
   };

   const handleRadioReportClassChange = (event) => {
      const value = parseInt(event.target.value);
      setReportPayload({
         ...reportPayload,
         ReportClass: value,
      });
   };

   const handleRadioReportShiftChange = (event) => {
      const value = parseInt(event.target.value);
      setReportPayload({
         ...reportPayload,
         ReportShift: value,
      });
   };

   const handleSideOptionClick = (e) => {
      const currentValue = e.target.value;
      if (selectedOption === currentValue) return;
      setSelectedSide(currentValue);
   };

   const handleTimeChange = (FromOrTo = "To", e) => {
      if (_.isNil(e)) return;
      const inputValue = e.target.value;
      const regex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/; // Regex for HH:mm format
      if (regex.test(inputValue)) {
         if (FromOrTo === "To") {
            setTimeRange({ ...timeRange, To: inputValue, ValidTo: true });
         } else {
            setTimeRange({ ...timeRange, From: inputValue, ValidFrom: true });
         }
      } else {
         if (FromOrTo === "To") {
            setTimeRange({ ...timeRange, ValidTo: false });
         } else {
            setTimeRange({ ...timeRange, ValidFrom: false });
         }
      }
   };

   const setCurrentItem = (opt) => {
      setMachineInfo({
         MachineSelect: null,
         MachineNumber: null,
         ToAssign: 0,
         Side: "",
      });

      setItem(opt);
   };

   const [hours, overFlow] = useMemo(() => {
      function calculateHourDifference(start, end) {
         let overFlow = false;
         // Split the start and end times into hours and minutes
         const [startHour, startMinute] = start.split(":").map(Number);
         const [endHour, endMinute] = end.split(":").map(Number);
         // Convert the start and end times to total minutes
         const startTotalMinutes = startHour * 60 + startMinute;
         const endTotalMinutes = endHour * 60 + endMinute;
         if (startTotalMinutes === endTotalMinutes) return [24, true];
         let minuteDifference = endTotalMinutes - startTotalMinutes;
         // If the end time is before the start time, add 24 hours to the difference
         if (minuteDifference < 0) {
            overFlow = true;
            minuteDifference += 24 * 60;
         }
         // Convert the total minute difference to hours
         const hourDifference = Math.floor(minuteDifference / 60);

         return [hourDifference, overFlow]; //overFlow means that the end Time is before the start time
         // meaning that the shift has started on one day and will end on the next day
      }

      if (!timeRange.From || !timeRange.To) return 0;

      return calculateHourDifference(timeRange.From, timeRange.To);
   }, [timeRange.From, timeRange.To]);

   const days = useMemo(() => {
      function calculateDays(date1, date2) {
         date1.setHours(0, 0, 0, 0);
         date2.setHours(0, 0, 0, 0);

         // Calculate the difference in days
         const diffInTime = Math.abs(date2.getTime() - date1.getTime());
         const diffInDays = Math.ceil(diffInTime / (1000 * 60 * 60 * 24));

         return diffInDays;
      }

      if (!dateRange.Start || !dateRange.End) return 1;
      const result = calculateDays(dateRange.Start, dateRange.End);
      return result < 1 ? 0 : result;
   }, [dateRange.Start, dateRange.End]);

   useEffect(() => {
      async function fetchData() {
         document.title = `Production Schedule`;
         let displayDay = days + 1;
         if (overFlow) displayDay -= 1;
         let totalHours = hours ? hours * displayDay : 0;
         if (overFlow && days < 1) {
            displayDay = 0;
            totalHours = 0;
         }
         setTimeInterval({
            days: displayDay,
            hours: hours,
            totalHours: totalHours,
         });

         let PPH = 0;
         try {
            PPH = await CalculatePPHLocally();
         } catch (error) {
            toast.error(error, "pph is missing");
         }

         if (
            !_.isNil(item?.value) ||
            !_.isNil(machineInfo?.MachineSelect?.value)
         ) {
            setPPHInterval({
               pph: PPH,
               pounds: _.round(PPH * hours * (machineInfo.ToAssign / ends), 2),
               totalPounds: _.round(PPH * totalHours * (machineInfo.ToAssign / ends), 2),
            });
         } else {
            setPPHInterval({ pph: 0, pounds: 0, totalPounds: 0 });
         }
      }

      fetchData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [timeRange, dateRange, refreshData, machineInfo]);

   useEffect(() => {
      if (!_.isNil(item?.itemNumber) && !_.isNil(machineInfo?.MachineSelect?.value)) {
         setIsLoading(true);
         api
            .fetch(`MachineSetup/GetCabledEndsByItemAndType/${item.itemNumber}/${machineInfo.MachineSelect.value}`)
            .then((r) => {
               if (r.data.success) {
                  if (r.data.cabledEnds > 0)
                     setEnds(r.data.cabledEnds);
                  else
                     setEnds(1);
               } else {
                  setEnds(1);
                  toast.error("Unable to determine the number of cabled ends");
               }
            })
            .catch(api.catchHandler)
            .finally(() => {
               setIsLoading(false);
            });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [machineInfo?.MachineSelect]);

   const { Success, ClassList } = useGetItemClasses({
      IsInProcessBulkClassCode: true,
      IsFinishedGoodClassCode: true,
   });

   let ClassListFormatted = useMemo(() => {
      let list = [];
      if (Success) {
         list = _.map(ClassList, (x) => {
            x.label = `${x.value} | ${x.label}`;
            return x;
         });
      }

      return list;
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [Success]);

   const machinePlan = useMemo(() => {
      function CalculateMachineDto() {
         const MachineIdTmp = machineInfo.MachineSelect?.value || 0;
         const MachineNumTmp = machineInfo?.MachineNumber?.value || 0;

         let plan = {
            // DateFrom: new Date(`${dateString1} ${timeRange.From}`).toISOString(),
            // DateTo: finalTo, //setDAteFunc
            DateFrom: combineDateTime(dateRange.Start, timeRange.From),
            DateTo: combineDateTime(dateRange.End, timeRange.To),
            ItemNumber: hideItem
               ? constants.SCHEDULED_MAINTENANCE
               : item?.value || "",
            MachineId: MachineIdTmp,
            MachineNum: MachineNumTmp,
            Side: "",
            SpindlesUsed: machineInfo.ToAssign < 0 ? 0 : machineInfo.ToAssign,
            PPH: 0,
            ItemClass: helpers.getCommaDelimitedString(itemClassCodes),
            //use to be this ItemClass: helpers.getCommaDelimitedString(itemClass), but item classes was never being set
         };
         return plan;
      }

      return CalculateMachineDto();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [
      item,
      machineInfo.MachineSelect,
      hideItem,
      machineInfo.MachineNumber,
      dateRange,
      timeRange,
   ]);

   const loadOptions = async (_inputValue, callback) => {
      const options = MachineSetupList;
      callback(options);
   };

   const loadOptions2 = async (_inputValue, callback) => {
      const options = ActiveMachineList;
      callback(options);
   };

   const { ActiveMachineList, ActiveMachineSuccess, ShowSides } = useGetActiveMachines(
      machineInfo.MachineSelect,
      machinePlan,
      timeInterval.days,
      ""
   );

   // Re-derive whenever the chosen side changes too, so the availability query
   // below re-runs for the side actually selected (twin-side machines).
   const sideForSpindles = useMemo(
      () => (ShowSides ? selectedSide : ""),
      [ShowSides, selectedSide]
   );

   const { TotalSpindleLoading, TotalAvailable } = useGetTotalSpindles(
      machineInfo,
      machinePlan,
      timeInterval.days,
      sideForSpindles
   );

   const {
      ScheduledData,
      UnScheduledData,
      PlanningIsLoading,
      TotalSpindlesUsed,
      TotalExpecBuildQty,
      TotalLaborHours,
   } = useGetPlanningProdGridData(
      machinePlan,
      viewUnScheduled,
      refreshData,
      dateRange,
      timeRange,
      overFlow
   );

   useEffect(() => {
      if (ActiveMachineList && ActiveMachineList.length && machineInfo.MachineNumber) {
         let sameMachineNumber = _.find(ActiveMachineList, am => am.value === machineInfo.MachineNumber.value);
         if (sameMachineNumber && sameMachineNumber.label !== machineInfo.MachineNumber.label) {
            setMachineInfo({ ...machineInfo, MachineNumber: sameMachineNumber });
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [ActiveMachineList]);

   const { MachineSetupList, MachineSetupSuccess, Specification } = useGetMachineSetupForPlanning(item);

   const updateRowStart = (selectedItem) => {
      if (isLoading) return;
      setReasonId(0);  // reset
      setSubId(0);     // reset
      setComment("");  // reset

      if (!selectedItem.dateFrom) {
         setUpdateItem(null);
         toast.error("Inavlid from date");
         return;
      }
      if (!selectedItem.dateTo) {
         setUpdateItem(null);
         toast.error("Inavlid to date");
         return;
      }
      if (new Date(selectedItem.dateFrom) > new Date(selectedItem.dateTo)) {
         setUpdateItem(null);
         toast.error("To date must be after the from date");
         return;
      }
      setUpdateItem(selectedItem);
      setUpdateId(selectedItem.id); // this will cause the dialog for reason code and comment to show up
   };

   const updateRow = () => {
      // validations
      if (reasonId <= 0) {
         toast.error("A reason must be selected");
         return;
      }
      if (reasonSubId <= 0) {
         toast.error("A problem must be selected");
         return;
      }

      const payload = {
         ...updateItem,         
      };
      payload.reasonId = reasonId;
      payload.reasonSubId = reasonSubId;
      payload.note = comment;

      setIsLoading(true);
      api
         .post("Production/UpdateSchedule", payload)
         .then((r) => {
            if (r.data.success) {
               setUpdateId(0);
               setRefreshData(!refreshData);
            } else {
               toast.error(r.data.message || "Something went wrong while attempting to update");
               // Close the dialog and reload, so the next Edit opens on the row as it is now - a "row changed"
               // refusal would otherwise repeat on every retry.
               setUpdateId(0);
               setRefreshData(!refreshData);
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setIsLoading(false);
            if (selectedEdit.index < 0)
               cancelRow(setMaintenanceEdit);
            else
               cancelRow(setSelectedEdit);
         });
   };

   const deleteRowStart = (x) => {
      setReasonId(0);  // reset
      setSubId(0);     // reset
      setComment("");  // reset
      setEditId(x.id); // this will cause the dialog for reason code and comment to show up
      setDeleteSeen(stampSeenWindow(x));
   };

   const deleteRow = () => {
      // validations
      if (reasonId <= 0) {
         toast.error("A reason must be selected");
         return;
      }
      if (reasonSubId <= 0) {
         toast.error("A problem must be selected");
         return;
      }

      let payload = Object.assign({}, DELETE_DTO);
      payload.Id = editId;
      payload.ReasonId = reasonId;
      payload.reasonSubId = reasonSubId;
      payload.Note = comment;
      if (deleteSeen) {
         payload.seenDateFrom = deleteSeen.seenDateFrom;
         payload.seenDateTo = deleteSeen.seenDateTo;
         payload.seenSpindles = deleteSeen.seenSpindles;
      }

      setIsLoading(true);
      api
         .post("Production/DeleteSchedule", payload)
         .then((r) => {
            if (r.data.success) {
               setEditId(0);
               setRefreshData(!refreshData);
            } else {
               toast.error(r.data.message || "Something went wrong while attempting to delete");
               // Same as updateRow - start the next Delete from the reloaded row.
               setEditId(0);
               setRefreshData(!refreshData);
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setIsLoading(false);
         });
   };

   const cancelRow = (stateCallback) => {
      stateCallback({
         index: -1,
      });
   };

   const customStyles = {
      menuList: (provided, state) => ({
         ...provided,
         maxHeight: "150px", // Adjust this value to control the height of the scrollable area
         overflowY: "auto",
      }),
   };

   async function CalculatePPHLocally() {
      let finalPPH = 0;
      if (_.isNil(item) || _.isNil(machineInfo?.MachineSelect)) return;
      await api
         .post(`Production/CalculatePPHProductionSchedule`, {
            ItemNumber: item.value,
            MachineId: machineInfo.MachineSelect.value,
         })
         .then((r) => {
            if (r.data.success) {
               finalPPH = r.data.calcPPS;
            } else {
               toast.error(
                  "Something went wrong, unable to calculate pph while attempting to assign spindles"
               );
            }
         })
         .catch(api.catchHandler);
      return finalPPH;
   }

   async function saveSchedule(plan) {
      api
         .post(`Production/SaveSchedule`, {
            ...plan,
            ToAssign: hideItem
               ? parseInt(TotalAvailable)
               : parseInt(machineInfo.ToAssign),
            Side: ShowSides ? selectedSide : "",
         })
         .then((r) => {
            if (r.data.success) {
               toast.success("Successfully Assigned!");
               setRefreshData(!refreshData);
               setCurrentItem(null);
            } else {
               if (r.data?.message?.length > 0)
                  toast.error(r.data.message);
               else
                  toast.error("Something went wrong while Assigning Spindles contact your Admin.");
            }
         })
         .catch(api.catchHandler)
         .finally(() => { });
   }

   const validatedFields = (plan) => {
      if (_.isEmpty(plan)) {
         toast.error("The machine plan is empty, please contact your admin.");
      }

      let validated = true;
      if (plan.DateFrom > plan.DateTo) {
         toast.error(`The from date must be prior to the end date`);
         validated = false;
      }
      if (!hideItem && !plan.ItemNumber) {
         toast.error(`ItemNumber is a required field to Assign spindles`);
         validated = false;
      }
      if (_.isNil(machineInfo.MachineNumber)) {
         toast.error(`Machine Number is a required field to Assign spindles`);
         validated = false;
      }
      if (_.isNil(machineInfo.MachineSelect)) {
         toast.error(`Machine is a required field to Assign spindles`);
         validated = false;
      }
      if (!machineInfo.ToAssign) {
         toast.error(`Spindles is a required field to Assign spindles`);
         validated = false;
      }
      if (!machineInfo.ToAssign && !hideItem) {
         toast.error(`Spindles is a required field to Assign spindles`);
         validated = false;
      }
      if (machineInfo.ToAssign > TotalAvailable) {
         toast.error(`Assign spindles amount is greater than total available`);
         validated = false;
      }
      return validated;
   };

   async function assignSpindles() {
      if (isLoading) return;
      setIsLoading(true);
      const startDateBeforeEndDate = dateRange.Start < dateRange.End;
      if (overFlow && !startDateBeforeEndDate) {
         toast.error(`Start date must be before end date.`);
         setIsLoading(false);
         return;
      }
      let plan = _.cloneDeep(machinePlan);
      plan.Days = timeInterval.days;
      // if(timeInterval.hours && timeInterval.hours === 24){
      //   plan.Days += 1;
      // }    
      if (!validatedFields(plan)) {
         setIsLoading(false);
         return;
      }
      const PPH = await CalculatePPHLocally();
      plan.PPH = PPH; // this is happening twice until I can figure out a way to call it when I need to and not inside the MachinePlan const
      if (machinePlan.PPH <= 0 && hideItem) {
         const PPH = await CalculatePPHLocally();
         plan.PPH = PPH;
      }

      //if hideItem is true, then ItemNumber will be "Scheduled Maintenance" then we are more than likely going to schedule
      if (hideItem && ShowSides) {
         //Scheduled maintenance takes down the whole machine, a machine with sides needs a record for each side/date combination
         //Save record(s) for side a
         plan.Side = "A";
         await saveSchedule(plan);

         //Reset plan dates and save record(s) for side B
         // SetDateTo(plan); this should already be happening all the time but, if I need to make this it's own function I will later
         plan.Side = "B";
         await saveSchedule(plan);
      } else {
         // For machines being scheduled for something other than maintenance the user must select side A or B if applicable, so only one side will be done at a time.
         await saveSchedule(plan);
      }
      setIsLoading(false);
   }

   useEffect(() => {
      if (hideItem) {
         GetMachinesScheduledForMaintenance();
      } else {
         setScheduledMaintenanceList([]);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [hideItem, timeRange, dateRange, machineInfo, isLoading]);

   function GetMachinesScheduledForMaintenance() {
      const payload = {
         ...machinePlan,
      };
      if (payload.DateFrom > payload.DateTo) {
         toast.error(`The from date must be prior to the end date`);
         return;
      }
      api
         .post("Production/GetMachinePlanningScheduledMaintenance", payload)
         .then((r) => {
            if (r.data && r.data.success) {
               setScheduledMaintenanceList(r.data.gridData);
            } else {
               toast.error(`Error: ${r.data.message}`);
            }
         })
         .catch((error) => {
            console.error(error);
            toast.error(
               "an error has occured, could not retrieve scheduled maintenance list"
            );
         })
         .finally(() => {
            // setIsLoading(false);
         });
   }

   function ExportMachinePlanningCSV() {
      setIsLoading(true);
      const payload = {
         ...machinePlan,
      };
      if (payload.DateFrom > payload.DateTo) {
         toast.error(`The from date must be prior to the end date`);
         return;
      }
      api
         .postGetBlob("Production/ExportMachinePlanningCSV", payload)
         .then((r) => {
            if (r.data && r.status === 200) {
               helpers.downloadExport(
                  r.data,
                  `Machine Planning ${dateHelpers.toLongDateString(dateHelpers.monthDayAndYear(Date.now()))}.xlsx`,
                  constants.MIME_TYPES.xlsx
               );
            } else {
               toast.error(
                  r.message || "Something went wrong, contact your admin"
               );
            }
         })
         .finally(() => setIsLoading(false));
   }

   function ExportShiftPlanning() {
      setIsLoading(true);
      const payload = {
         ...machinePlan,
      };
      if (payload.DateFrom > payload.DateTo) {
         toast.error(`The from date must be prior to the end date`);
         return;
      }
      api
         .postGetBlob("Production/ExportPlanningSchedule", payload)
         .then((r) => {
            if (r.data && r.status === 200) {
               helpers.downloadExport(
                  r.data,
                  `Shift Planning ${dateHelpers.toLongDateString(dateHelpers.monthDayAndYear(Date.now()))}.xlsx`,
                  constants.MIME_TYPES.xlsx
               );
            } else {
               toast.error(
                  r.message || "Something went wrong, contact your admin"
               );
            }
         })
         .finally(() => setIsLoading(false));
   }

   function combineDateTime(orig, time) {
      let date = new Date(orig.getTime());
      let timeArr = time.split(":");
      //let offset = date.getTimezoneOffset();
      //offset = offset / 60;
      let hours = parseInt(timeArr[0]);
      let minutes = parseInt(timeArr[1]);
      date.setHours(hours, minutes, 0);
      let momentDate = dateHelpers.momentFromString(date, "MM/DD/YYYY HH:mm:ss A");
      // let momentDateOffsetted = momentDate.subtract(offset, 'hours'); //Leaving timezone in UTC to be parsed on server
      return momentDate;
   }

   function handleRunReport() {
      if (isLoading) return;

      let formattedEndDate = combineDateTime(dateRange.End, timeRange.To);
      let formattedStartDate = combineDateTime(dateRange.Start, timeRange.From);

      let payload = {
         Plan: { ...machinePlan },
         ReportClass: reportPayload.ReportClass,
         PrintPDF: reportPayload.ReportType === REPORT_FILE_TYPES.PDF,
         EightHourShift: reportPayload.ReportShift === SHIFT_TYPES.EightHour,
         StartTime: formattedStartDate,
         EndTime: formattedEndDate
      };
      if (!ScheduledData.length) {
         toast.warning("No schedule data found for report.");
         return;
      }
      if (payload.Plan.DateFrom > payload.Plan.DateTo) {
         toast.error(`The from date must be prior to the end date`);
         return;
      }

      setIsLoading(true);
      if (payload.PrintPDF) {
         api
            .postGetBlob(
               "Report/GetProductionScheduleReporting",
               payload,
               "Error downloading pdf"
            )
            .then((response) => {
               if (response.data === false) {
                  if (_.isString(response?.message)) {
                     toast.error(response?.message);
                  }
                  return;
               }

               if (response.data) {
                  const fileName = response.headers["content-disposition"]
                     .match(/filename=([\s\S]*);/)[1]
                     .replaceAll('"', "");
                  const file = new Blob([response.data], {
                     type: response.data.type,
                  });
                  var anchor = window.document.createElement("a");
                  anchor.download = fileName;
                  anchor.href = (window.webkitURL || window.URL).createObjectURL(
                     file
                  );
                  anchor.dataset.downloadurl = [
                     response.data.type,
                     anchor.download,
                     anchor.href,
                  ].join(":");
                  anchor.click();
               } else {
                  throw new Error("Error downloading pdf");
               }
            })
            .catch((error) => {
               console.error(error);
               toast.error("an error has occured, please contact your admin");
            })
            .finally(() => {
               setIsLoading(false);
            });
      } else {
         api
            .postGetBlob(
               "Report/GetProductionScheduleReporting",
               payload,
               "Error downloading csv"
            )
            .then((response) => {
               if (response.data) {
                  if (response.data.success === false) {
                     if (_.isString(response?.data?.message)) {
                        toast.error(response?.data?.message);
                     }
                     return;
                  }
                  const fileName = response.headers["content-disposition"]
                     .match(/filename=([\s\S]*);/)[1]
                     .replaceAll('"', "");
                  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                     window.navigator.msSaveOrOpenBlob(response.data, fileName);
                  } else {
                     const file = new Blob([response.data], {
                        type: response.data.type,
                     });
                     var anchor = window.document.createElement("a");
                     anchor.download = fileName;
                     anchor.href = (window.webkitURL || window.URL).createObjectURL(
                        file
                     );
                     anchor.dataset.downloadurl = [
                        response.data.type,
                        anchor.download,
                        anchor.href,
                     ].join(":");
                     anchor.click();
                  }
               } else {
                  toast.error(response.message || "Error downloading csv");
               }
            })
            .catch((error) => toast.error(error))
            .finally(() => {
               setIsLoading(false);
            });
      }
   }

   const displayPPHFields = Object.values(pphInterval).every(property => property);

   function handleMachineSpindleToAssign(value = 0) {
      value = value > TotalAvailable ? TotalAvailable : value;
      value = value < 0 ? 0 : value;
      setMachineInfo({
         ...machineInfo,
         ToAssign: value,
      });
   }

   useEffect(() => {
      handleMachineSpindleToAssign(TotalAvailable);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [TotalAvailable]);

   function GetSelectedByIndex(index, selectedItem) {
      return index === selectedItem?.index;
   }

   return (
      <DesktopContainer screenName={"Production Schedule"}>
         <div className="filter-header mb-3">
            {canEdit &&
               <Row className="mb-2">
                  <Col
                     xxl={{ offset: 4, size: 3 }}
                     className="text-white"
                     style={{ display: "flex", justifyContent: "space-between" }}
                  >
                     <div className="d-inline-flex align-items-center">
                        <input
                           type="radio"
                           id="maintenance"
                           name="option"
                           className="square-radio"
                           value="maintenance"
                           defaultChecked={selectedOption === "maintenance"}
                           onClick={handleOptionClick}
                        />
                        <label htmlFor="maintenance">
                           Set Time for Scheduled Maintenance?
                        </label>
                     </div>
                     <div className="d-inline-flex align-items-center">
                        <input
                           type="radio"
                           id="unscheduled"
                           name="option"
                           className="square-radio"
                           value="unscheduled"
                           defaultChecked={selectedOption === "unscheduled"}
                           onClick={handleOptionClick}
                        />
                        <label htmlFor="unscheduled">View Unscheduled Machines?</label>
                     </div>
                  </Col>
               </Row>
            }
            {showItemFilter &&
               <Row className={classNames("mb-2", { "d-none": hideItem })}>
                  <ColPrompt title={"Item"} />
                  <ColValue size={3}>
                     <ItemTypeahead value={item} onChange={(opt) => setCurrentItem(opt)} />
                  </ColValue>
               </Row>
            }
            {canEdit &&
               <div>
                  <Row className="mb-2">
                     <ColPrompt title={"Machine"} />
                     <ColValue size={3}>
                        <AsyncSelect
                           cacheOptions
                           defaultOptions={MachineSetupList}
                           loadOptions={loadOptions}
                           classNamePrefix="serviceThreadSelect"
                           isLoading={!MachineSetupSuccess || Specification.loading}
                           isDisabled={!MachineSetupSuccess || Specification.loading}
                           isClearable={true}
                           onChange={(opt) => opt
                              ? setMachineInfo({
                                 ...machineInfo,
                                 MachineSelect: opt,
                                 MachineNumber: null,
                              })
                              : setMachineInfo({
                                 ...machineInfo,
                                 MachineSelect: null,
                                 MachineNumber: null,
                                 ToAssign: 0,
                                 Side: "",
                              })
                           }
                           value={
                              Specification?.oneMachineSetupOnly
                                 ? Specification.singleMachine
                                 : machineInfo.MachineSelect
                           }
                        />
                     </ColValue>
                     <Col
                        className={classNames("text-white", { "d-none": !ShowSides })}
                        xxl={1}
                        style={{ display: "flex", alignItems: "center" }}
                     >
                        <h5 className="mb-0 me-3">Side:</h5>
                        {/* reactstrap form-check radios (matching the report radios below) so the
                            checked side shows the Bootstrap primary fill instead of a bare native
                            radio that blends into the dark card background. */}
                        <FormGroup check inline className="mb-0">
                           <Label check style={{ fontWeight: selectedSide === "A" ? 700 : 400 }}>
                              <Input
                                 type="radio"
                                 name="side"
                                 value="A"
                                 checked={selectedSide === "A"}
                                 onChange={handleSideOptionClick}
                              />{" "}
                              A
                           </Label>
                        </FormGroup>
                        <FormGroup check inline className="mb-0">
                           <Label check style={{ fontWeight: selectedSide === "B" ? 700 : 400 }}>
                              <Input
                                 type="radio"
                                 name="side"
                                 value="B"
                                 checked={selectedSide === "B"}
                                 onChange={handleSideOptionClick}
                              />{" "}
                              B
                           </Label>
                        </FormGroup>
                     </Col>
                  </Row>
                  <Row className="mb-2" style={{ display: "flex", alignItems: "center" }}>
                     <ColPrompt title={"Machine #"} />
                     <Col xxl={3}>
                        <AsyncSelect
                           cacheOptions
                           defaultOptions={ActiveMachineList}
                           loadOptions={loadOptions2}
                           classNamePrefix="serviceThreadSelect"
                           isClearable={true}
                           isLoading={!ActiveMachineSuccess || TotalSpindleLoading}
                           isDisabled={!ActiveMachineSuccess}
                           onChange={(opt) => opt
                              ? setMachineInfo({ ...machineInfo, MachineNumber: opt })
                              : setMachineInfo({ ...machineInfo, MachineNumber: null })
                           }
                           value={machineInfo.MachineNumber}
                        />
                     </Col>
                     <Col>
                        <h5 className="pt-1 text-white ms-2">
                           Total Available Spindles: {TotalAvailable}
                        </h5>
                     </Col>
                  </Row>
                  <Row className="mb-2">
                     <ColPrompt title={"Spindles To Assign"} />
                     <ColValue
                        size={3}
                        children={
                           <Input
                              name=""
                              type="number"
                              disabled={hideItem || !TotalAvailable}
                              max={TotalAvailable}
                              value={
                                 hideItem && machineInfo.MachineNumber
                                    ? TotalAvailable
                                    : machineInfo.ToAssign
                              }
                              onChange={(e) => {
                                 let value = e ? e.target.value : 0;
                                 handleMachineSpindleToAssign(value);
                              }}
                           />
                        }
                     />
                  </Row>
               </div>
            }

            <Row className="mb-2">
               <ColPrompt title={"Date Range"} />
               <Col xxl={1}>
                  <DatePicker
                     className="form-control"
                     selected={dateRange.Start}
                     onChange={(e) => setDateRange({ ...dateRange, Start: new Date(e.setHours(0, 0, 0, 0)) })}
                     dateFormat="MM-dd-yyyy"
                     placeholderText="Select a date"
                  />
               </Col>
               <Col xxl={{ offset: 1, size: 1 }}>
                  <DatePicker
                     className="form-control"
                     selected={dateRange.End}
                     onChange={(e) => setDateRange({ ...dateRange, End: new Date(e.setHours(0, 0, 0, 0)) })}
                     dateFormat="MM-dd-yyyy"
                     placeholderText="Select a date"
                  />
               </Col>
            </Row>
            <Row className="mb-2">
               <ColPrompt title={"Time Range From"} />
               <Col xxl={1}>
                  <DebounceInput
                     type="text"
                     value={timeRange.From}
                     onChange={(e) => handleTimeChange("From", e)}
                     debounceTimeout={300}
                     className="form-control"
                  />
               </Col>
               <Col xxl={{ offset: 1, size: 1 }} className={"timeIntervalDisplay"} style={{ textAlign: "right" }} >
                  {timeInterval.days} day(s)
               </Col>
               {!timeRange.ValidFrom && (
                  <ErrorDiv>Please correct the time format (HHH:MM)</ErrorDiv>
               )}
               <Col className={"ms-2"}>
                  {displayPPHFields
                     ? (<div className="timeIntervalDisplay">{pphInterval.pph} PPH</div>)
                     : null
                  }
               </Col>
            </Row>
            <Row className="mb-2">
               <ColPrompt title={"Time Range To"} />
               <Col xxl={1}>
                  <DebounceInput
                     type="text"
                     value={timeRange.To}
                     onChange={(e) => handleTimeChange("To", e)}
                     debounceTimeout={300}
                     className="form-control"
                  />
               </Col>
               <Col xxl={{ offset: 1, size: 1 }} className={"timeIntervalDisplay"} style={{ textAlign: "right" }} >
                  {timeInterval.hours} hour(s)
               </Col>
               {!timeRange.ValidTo && (
                  <ErrorDiv>Please correct the time format (HHH:MM)</ErrorDiv>
               )}
               <Col className={"ms-2"}>
                  {displayPPHFields
                     ? (<div className="timeIntervalDisplay">{pphInterval.pounds} pounds</div>)
                     : null
                  }
               </Col>
            </Row>
            <Row>
               <Col xxl={{ offset: 4, size: 3 }}>
                  <div className="time-picker-container2">
                     <Button
                        className={"shredButton float-start"}
                        children={"Assign Spindles"}
                        onClick={() => assignSpindles()}
                        disabled={
                           Specification.loading ||
                           !ActiveMachineSuccess ||
                           TotalSpindleLoading ||
                           !TotalAvailable
                        }
                     />
                     {timeInterval.totalHours !== undefined && (
                        <div className={"ms-2"}>{timeInterval.totalHours} total hour(s)</div>
                     )}
                  </div>
               </Col>
               <Col className={"ms-2"}>
                  {displayPPHFields
                     ? (<div className="timeIntervalDisplay">{pphInterval.totalPounds} total pounds</div>)
                     : null
                  }
               </Col>
            </Row>

            {showRpts &&
               <div>
                  <Row>
                     <Col xxl={{ offset: 3, size: 6 }}>
                        <hr />
                     </Col>
                  </Row>
                  <Row>
                     <Col xxl={{ offset: 3, size: 10 }}>
                        <h6 className="text-white">
                           If one or more classes are selected the report will only contain
                           items in those class(es)
                        </h6>
                     </Col>
                  </Row>
                  <Row>
                     <ColPrompt title={"Item Class"} />
                     <Col xxl={3}>
                        <Select
                           classNamePrefix="serviceThreadSelect"
                           options={ClassListFormatted}
                           value={itemClassCodes}
                           onChange={(e) => _.isNil(e) ? setItemClassCodes([]) : setItemClassCodes(e)}
                           styles={customStyles}
                           menuIsOpen={true}
                           isMulti={true}
                        />
                     </Col>
                     <Col className="text-white">
                        <Row className="mb-3">
                           <Col>
                              <h5>Report Option:</h5>
                              <FormGroup check inline>
                                 <Label check>
                                    <Input
                                       type="radio"
                                       name="reportOption"
                                       value={REPORT_CLASS_TYPES.ByItem}
                                       checked={reportPayload.ReportClass === REPORT_CLASS_TYPES.ByItem}
                                       onChange={handleRadioReportClassChange}
                                    />{" "}
                                    By Item
                                 </Label>
                              </FormGroup>
                              <FormGroup check inline>
                                 <Label check>
                                    <Input
                                       type="radio"
                                       name="reportOption"
                                       value={REPORT_CLASS_TYPES.ByMachine}
                                       checked={reportPayload.ReportClass === REPORT_CLASS_TYPES.ByMachine}
                                       onChange={handleRadioReportClassChange}
                                    />{" "}
                                    By Machine
                                 </Label>
                              </FormGroup>
                              <FormGroup check inline>
                                 <Label check>
                                    <Input
                                       type="radio"
                                       name="reportOption"
                                       value={REPORT_CLASS_TYPES.ByDay}
                                       checked={reportPayload.ReportClass === REPORT_CLASS_TYPES.ByDay}
                                       onChange={handleRadioReportClassChange}
                                    />{" "}
                                    By Day
                                 </Label>
                              </FormGroup>
                           </Col>
                        </Row>
                        <Row className="mb-3">
                           <Col>
                              <h5>Report Type:</h5>
                              <FormGroup check inline>
                                 <Label check>
                                    <Input
                                       type="radio"
                                       name="reportType"
                                       value={REPORT_FILE_TYPES.PDF}
                                       checked={reportPayload.ReportType === REPORT_FILE_TYPES.PDF}
                                       onChange={handleRadioChange}
                                    />{" "}
                                    PDF
                                 </Label>
                              </FormGroup>
                              <FormGroup check inline>
                                 <Label check>
                                    <Input
                                       type="radio"
                                       name="reportType"
                                       value={REPORT_FILE_TYPES.XLSX}
                                       checked={reportPayload.ReportType === REPORT_FILE_TYPES.XLSX}
                                       onChange={handleRadioChange}
                                    />
                                    XLS
                                 </Label>
                              </FormGroup>
                           </Col>
                        </Row>
                        <Row className="mb-3">
                           <Col>
                              <h5>Shift Type:</h5>
                              <FormGroup check inline>
                                 <Label check>
                                    <Input
                                       type="radio"
                                       name="shiftType"
                                       value={SHIFT_TYPES.TwelveHour}
                                       checked={reportPayload.ReportShift === SHIFT_TYPES.TwelveHour}
                                       onChange={handleRadioReportShiftChange}
                                    />{" "}
                                    12 Hour Shifts
                                 </Label>
                              </FormGroup>
                              <FormGroup check inline>
                                 <Label check>
                                    <Input
                                       type="radio"
                                       name="shiftType"
                                       value={SHIFT_TYPES.EightHour}
                                       checked={reportPayload.ReportShift === SHIFT_TYPES.EightHour}
                                       onChange={handleRadioReportShiftChange}
                                    />{" "}
                                    8 Hour Shifts
                                 </Label>
                              </FormGroup>
                           </Col>
                        </Row>
                     </Col>
                  </Row>
                  <Row>
                     <Col xxl={{ offset: 7, size: 3 }}>
                        <Button className="shredButton float-start" onClick={() => handleRunReport()} >
                           Run Report
                        </Button>
                     </Col>
                  </Row>
               </div>
            }
         </div>

         <Row className={classNames("mb-2")}>
            <Col>
               <ServiceThreadSpinner on={PlanningIsLoading || isLoading}>
                  {!hideItem && !viewUnScheduled && selectedEdit && (!_.isEmpty(ScheduledData) ? (
                     <>
                        <Row className={"mb-2"}>
                           <Col xs={{ offset: 6, size: 6 }} className="transparent-background">
                              <ButtonGroup className="btn-group-sm float-end">
                                 <Button
                                    children="Shift Report"
                                    className="shredButton float-start"
                                    onClick={() => ExportShiftPlanning()}
                                 />
                                 {showRpts &&
                                    <Button
                                       title="Download as .xlsx"
                                       className="shredButtonExcel float-end ms-2"
                                       onClick={() => ExportMachinePlanningCSV()}
                                       disabled={isLoading}
                                    >
                                       <FontAwesomeIcon icon="file-excel" size="lg" />
                                    </Button>
                                 }
                              </ButtonGroup>
                           </Col>
                        </Row>
                        <Table size="sm" className="data-table">
                           <thead className="table-header">
                              <tr>
                                 <th>Item</th>
                                 <th>Machine Type</th>
                                 <th>Machine #</th>
                                 <th>Side</th>
                                 <th>Date From</th>
                                 <th>Date To</th>
                                 <th><span>Spindles<br />Used</span></th>
                                 <th><span>Expected<br />Build Qty</span></th>
                                 <th><span>Labor<br />Hours</span></th>
                                 {canEdit &&
                                    <th colSpan={2} />
                                 }
                              </tr>
                           </thead>
                           <tbody>
                              {_.map(ScheduledData, (x, index) => {
                                 const selectedRow = GetSelectedByIndex(index, selectedEdit);
                                 return (
                                    <tr key={index} className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`} >
                                       {x.itemNumber &&
                                          x.itemNumber === constants.SCHEDULED_MAINTENANCE ? (
                                          <td className="data-cell">
                                             <span className="shredLink fw-bold">{x.itemNumber}</span>
                                          </td>
                                       ) : (
                                          <ItemDropdownMenu
                                             itemNumber={x.itemNumber}
                                             itemDescription={x.itemWithDesc}
                                             attachmentCount={x.itemAttachmentCount}
                                          />
                                       )}
                                       <td className="data-cell">{x.machineName}</td>
                                       <td className="data-cell">{x.machineNum}</td>
                                       <td className="data-cell">{x.side}</td>
                                       {selectedRow ? (
                                          <td className="data-cell">
                                             <DatePicker
                                                className="form-control"
                                                selected={
                                                   selectedEdit.dateFrom
                                                      ? new Date(selectedEdit.dateFrom)
                                                      : new Date()
                                                }
                                                onChange={(date) => {
                                                   const newDate = new Date(date);
                                                   newDate.setSeconds(0);
                                                   const offset = newDate.getTimezoneOffset();
                                                   const localDate = new Date(newDate.getTime() - offset * 60 * 1000);
                                                   const formattedDate = localDate.toISOString().slice(0, -1);
                                                   setSelectedEdit({
                                                      ...selectedEdit,
                                                      dateFrom: formattedDate,
                                                   });
                                                }}
                                                dateFormat="MM-dd-yyyy h:mm aa"
                                                placeholderText="Select a date and time"
                                                // minDate={new Date()} - No restriction on minimum date
                                                showTimeSelect
                                                timeFormat="h:mm aa"
                                                timeIntervals={15}
                                                timeCaption="time"
                                             />
                                          </td>
                                       ) : (
                                          <td className="data-cell">
                                             {dateHelpers.toLongDateWithHours(x.dateFrom)}
                                          </td>
                                       )}
                                       {selectedRow ? (
                                          <td className="data-cell">
                                             <DatePicker
                                                className="form-control"
                                                selected={
                                                   selectedEdit.dateTo
                                                      ? new Date(selectedEdit.dateTo)
                                                      : new Date()
                                                }
                                                onChange={(date) => {
                                                   const newDate = new Date(date);
                                                   newDate.setSeconds(0);
                                                   const offset = newDate.getTimezoneOffset();
                                                   const localDate = new Date(newDate.getTime() - offset * 60 * 1000);
                                                   const formattedDate = localDate.toISOString().slice(0, -1);
                                                   setSelectedEdit({
                                                      ...selectedEdit,
                                                      dateTo: formattedDate,
                                                   });
                                                }}
                                                dateFormat="MM-dd-yyyy h:mm aa"
                                                placeholderText="Select a date and time"
                                                minDate={new Date(selectedEdit.dateFrom) || new Date()}
                                                showTimeSelect
                                                timeFormat="h:mm aa"
                                                timeIntervals={15}
                                                timeCaption="time"
                                             />
                                          </td>
                                       ) : (
                                          <td className="data-cell">
                                             {dateHelpers.toLongDateWithHours(x.dateTo)}
                                          </td>
                                       )}
                                       <td className="data-cell" width={"8%"}>
                                          {selectedRow ? (
                                             <Input
                                                name="SpindlesEdit"
                                                className=""
                                                type="number"
                                                onChange={(e) => {
                                                   setSelectedEdit({
                                                      ...selectedEdit,
                                                      spindlesUsed: e.target.value
                                                         ? parseInt(e.target.value)
                                                         : 0,
                                                   });
                                                }}
                                                value={
                                                   selectedEdit.spindlesUsed === ""
                                                      ? 0
                                                      : _.trimStart(selectedEdit.spindlesUsed, "0")
                                                }
                                             />
                                          ) : (
                                             x.spindlesUsed
                                          )}
                                       </td>
                                       <td className="data-cell">
                                          {helpers.formatNumberWithThousands(x.buildQty)}
                                       </td>
                                       <td className="data-cell">
                                          {helpers.roundNum(x.laborHours, 2)}
                                       </td>
                                       {canEdit &&
                                          <td className="data-cell">
                                             {selectedRow ? (
                                                <ButtonGroup className="btn-group-sm float-end">
                                                   <Button
                                                      children="Update"
                                                      color="success"
                                                      onClick={() => {
                                                         updateRowStart(selectedEdit);
                                                      }}
                                                   />
                                                   <Button
                                                      children="Cancel"
                                                      className="float-end"
                                                      onClick={() => {
                                                         cancelRow(setSelectedEdit);
                                                      }}
                                                   />
                                                </ButtonGroup>
                                             ) : (
                                                <Button
                                                   style={{ display: "block", marginLeft: "auto", marginRight: "auto", }}
                                                   color="primary"
                                                   onClick={() => {
                                                      setSelectedEdit({
                                                         index: index,
                                                         ...x,
                                                         ...stampSeenWindow(x),
                                                      });
                                                   }}
                                                >
                                                   Edit
                                                </Button>
                                             )}
                                          </td>
                                       }
                                       {canEdit &&
                                          <td className="data-cell">
                                             <Button
                                                style={{ display: "block", marginLeft: "auto", marginRight: "auto", }}
                                                children="Delete"
                                                color="danger"
                                                onClick={() => {
                                                   deleteRowStart(x);
                                                }}
                                             />
                                          </td>
                                       }
                                    </tr>
                                 );
                              })}
                           </tbody>
                           <tfoot className="table-footer">
                              <tr>
                                 <td colSpan={6} />
                                 <th>{TotalSpindlesUsed}</th>
                                 <th>{helpers.formatNumberWithThousands(TotalExpecBuildQty, 0)}</th>
                                 <th>{helpers.roundNum(TotalLaborHours, 2)}</th>
                                 {canEdit &&
                                    <td colSpan={2} />
                                 }
                              </tr>
                           </tfoot>
                        </Table>
                     </>
                  ) : (
                     <span>
                        <h5>Results Not Found</h5>
                     </span>
                  ))}

                  {viewUnScheduled && selectedEdit && (!_.isEmpty(UnScheduledData) ? (
                     <Row>
                        <Col xxl={{ offset: 3, size: 5 }}>
                           <Table size="sm" className="data-table text-center">
                              <thead className="table-header">
                                 <tr>
                                    <th>Machine</th>
                                    <th>Machine #</th>
                                    <th>Side</th>
                                    <th>Total Spindles</th>
                                    <th>Spindles Avail</th>
                                 </tr>
                              </thead>
                              <tbody>
                                 {_.map(UnScheduledData, (x, index) => {
                                    return (
                                       <tr key={index} className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`} >
                                          <td className="data-cell" children={x.machineName} />
                                          <td className="data-cell" children={x.machineNum} />
                                          <td className="data-cell" children={x.side} />
                                          <td className="data-cell" children={x.actualSpindles} />
                                          <td className="data-cell" children={x.availableSpindles} />
                                       </tr>
                                    );
                                 })}
                              </tbody>
                           </Table>
                        </Col>
                     </Row>
                  ) : (
                     <span>
                        <h5>Results Not Found</h5>
                     </span>
                  ))}

                  {hideItem && (scheduledMaintenanceList && scheduledMaintenanceList.length ? (
                     <Row>
                        <Col xxl={{ offset: 3, size: 6 }}>
                           <Table size="sm" className="data-table text-center">
                              <thead className="table-header">
                                 <tr>
                                    <th>Machine</th>
                                    <th>Machine #</th>
                                    <th>Side</th>
                                    <th>Spindles Assigned</th>
                                    <th>Date From</th>
                                    <th>Date To</th>
                                    {canEdit &&
                                       <th colSpan={2} />
                                    }
                                 </tr>
                              </thead>
                              <tbody>
                                 {_.map(scheduledMaintenanceList, (x, index) => {
                                    const selectedRow = GetSelectedByIndex(index, maintenanceEdit);
                                    return (
                                       <tr key={index} className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`} >
                                          <td className="data-cell" children={x.machineName} />
                                          <td className="data-cell" children={x.machineNum} />
                                          <td className="data-cell" children={x.side} />
                                          <td className="data-cell" width={"8%"}>
                                             {selectedRow ? (
                                                <Input
                                                   name="SpindlesEdit"
                                                   className=""
                                                   type="number"
                                                   onChange={(e) => {
                                                      setMaintenanceEdit({
                                                         ...maintenanceEdit,
                                                         spindlesUsed: e.target.value
                                                            ? parseInt(e.target.value)
                                                            : 0,
                                                      });
                                                   }}
                                                   value={
                                                      maintenanceEdit.spindlesUsed === ""
                                                         ? 0
                                                         : _.trimStart(maintenanceEdit.spindlesUsed, "0")
                                                   }
                                                />
                                             ) : (
                                                x.spindlesUsed
                                             )}
                                          </td>
                                          {selectedRow ? (
                                             <td className="data-cell">
                                                <DatePicker
                                                   className="form-control"
                                                   selected={
                                                      maintenanceEdit.dateFrom
                                                         ? new Date(maintenanceEdit.dateFrom)
                                                         : new Date()
                                                   }
                                                   onChange={(date) => {
                                                      const newDate = new Date(date);
                                                      newDate.setSeconds(0);
                                                      const offset = newDate.getTimezoneOffset();
                                                      const localDate = new Date(newDate.getTime() - offset * 60 * 1000);
                                                      const formattedDate = localDate.toISOString().slice(0, -1);
                                                      setMaintenanceEdit({
                                                         ...maintenanceEdit,
                                                         dateFrom: formattedDate,
                                                      });
                                                   }}
                                                   dateFormat="MM-dd-yyyy h:mm aa"
                                                   placeholderText="Select a date and time"
                                                   showTimeSelect
                                                   timeFormat="h:mm aa"
                                                   timeIntervals={15}
                                                   timeCaption="time"
                                                />
                                             </td>
                                          ) : (
                                             <td className="data-cell">
                                                {dateHelpers.toLongDateWithHours(x.dateFrom)}
                                             </td>
                                          )}
                                          {selectedRow ? (
                                             <td className="data-cell">
                                                <DatePicker
                                                   className="form-control"
                                                   selected={
                                                      maintenanceEdit.dateTo
                                                         ? new Date(maintenanceEdit.dateTo)
                                                         : new Date()
                                                   }
                                                   onChange={(date) => {
                                                      const newDate = new Date(date);
                                                      newDate.setSeconds(0);
                                                      const offset = newDate.getTimezoneOffset();
                                                      const localDate = new Date(newDate.getTime() - offset * 60 * 1000);
                                                      const formattedDate = localDate.toISOString().slice(0, -1);
                                                      setMaintenanceEdit({
                                                         ...maintenanceEdit,
                                                         dateTo: formattedDate,
                                                      });
                                                   }}
                                                   dateFormat="MM-dd-yyyy h:mm aa"
                                                   placeholderText="Select a date and time"
                                                   minDate={
                                                      new Date(maintenanceEdit.dateFrom) ||
                                                      new Date()
                                                   }
                                                   showTimeSelect
                                                   timeFormat="h:mm aa"
                                                   timeIntervals={15}
                                                   timeCaption="time"
                                                />
                                             </td>
                                          ) : (
                                             <td className="data-cell">
                                                {dateHelpers.toLongDateWithHours(x.dateTo)}
                                             </td>
                                          )}
                                          {canEdit &&
                                             <td className="data-cell">
                                                {selectedRow ? (
                                                   <ButtonGroup className="btn-group-sm float-end">
                                                      <Button
                                                         children="Update"
                                                         color="success"
                                                         onClick={() => { updateRowStart(maintenanceEdit); }}
                                                      />
                                                      <Button
                                                         children="Cancel"
                                                         className="float-end"
                                                         onClick={() => { cancelRow(setMaintenanceEdit); }}
                                                      />
                                                   </ButtonGroup>
                                                ) : (
                                                   <Button
                                                      style={{
                                                         display: "block",
                                                         marginLeft: "auto",
                                                         marginRight: "auto",
                                                      }}
                                                      color="primary"
                                                      onClick={() => {
                                                         setMaintenanceEdit({
                                                            index: index,
                                                            ...x,
                                                            ...stampSeenWindow(x),
                                                         });
                                                      }}
                                                   >
                                                      Edit
                                                   </Button>
                                                )}
                                             </td>
                                          }
                                          {canEdit &&
                                             <td className="data-cell">
                                                <Button
                                                   style={{ display: "block", marginLeft: "auto", marginRight: "auto", }}
                                                   children="Delete"
                                                   color="danger"
                                                   onClick={() => { deleteRowStart(x); }}
                                                />
                                             </td>
                                          }
                                       </tr>
                                    );
                                 })}
                              </tbody>
                           </Table>
                        </Col>
                     </Row>
                  ) : (
                     <span>
                        <h5>Results Not Found</h5>
                     </span>
                  ))}
               </ServiceThreadSpinner>
            </Col>
         </Row>


         {/* Document reason for deleting a schedule */}
         <Modal isOpen={editId !== 0} contentClassName={modalClassName}>
            <ModalHeader children="Document Reason for Deletion" />
            <ModalBody
               children={
                  <div>
                     <Row>
                        <Col className="width115px col-sm-2 h5">
                           <span>Reason:</span>
                        </Col>
                        <Col>
                           <FilterSelect
                              filterName="reasonId"
                              options={reasons}
                              value={filterHelpers.getValue(filters, "reasonId")}
                              onChangeCallback={(e) => onReasonChange(e)}
                           />
                        </Col>
                     </Row>
                     <Row className="lineheight8">&nbsp;</Row>
                     {reasonId && reasonId > 0 ? (
                        <Row>
                           <Col className="width115px col-sm-2 h5">
                              <span>Problem:</span>
                           </Col>
                           <Col>
                              <FilterSelect
                                 filterName="child"
                                 options={childSubs}
                                 value={filterHelpers.getValue(filters, "child")}
                                 onChangeCallback={e => onSubChange(e)}
                              />
                           </Col>
                        </Row>
                     ) : null}
                  </div>
               }
            />
            <ModalFooter>
               <ButtonGroup>
                  <Button children="Delete" className="button-success" onClick={() => { deleteRow(); }} />
                  <Button children="Cancel" className="button-danger" onClick={() => { setEditId(0); }} />
               </ButtonGroup>
            </ModalFooter>
         </Modal>


         {/* Document reason for a schedule change */}
         <Modal isOpen={updateId !== 0} contentClassName={modalClassName}>
            <ModalHeader children="Document Reason for Schedule Change" />
            <ModalBody
               children={
                  <div>
                     <Row>
                        <Col className="width115px col-sm-2 h5">
                           <span>Reason:</span>
                        </Col>
                        <Col>
                           <FilterSelect
                              filterName="reasonId"
                              options={reasons}
                              value={filterHelpers.getValue(filters, "reasonId")}
                              onChangeCallback={(e) => onReasonChange(e)}
                           />
                        </Col>
                     </Row>
                     <Row className="lineheight8">&nbsp;</Row>
                     {reasonId && reasonId > 0 ? (
                        <Row>
                           <Col className="width115px col-sm-2 h5">
                              <span>Problem:</span>
                           </Col>
                           <Col>
                              <FilterSelect
                                 filterName="child"
                                 options={childSubs}
                                 value={filterHelpers.getValue(filters, "child")}
                                 onChangeCallback={e => onSubChange(e)}
                              />
                           </Col>
                        </Row>
                     ) : null}
                  </div>
               }
            />
            <ModalFooter>
               <ButtonGroup>
                  <Button children="Update" className="button-success" onClick={() => { updateRow(); }} />
                  <Button children="Cancel" className="button-danger"
                     onClick={() => {
                        setUpdateId(0);
                        if (selectedEdit.index < 0)
                           cancelRow(setMaintenanceEdit);
                        else
                           cancelRow(setSelectedEdit);
                     }}
                  />
               </ButtonGroup>
            </ModalFooter>
         </Modal>

      </DesktopContainer>
   );
}
